import {
    FinancialOverviewChartDataPoint,
    FinancialOverviewChartEvent,
    FinancialOverviewChartMetric,
    FinancialOverviewTickerDataPoint,
} from '../../types';

export const MAX_RENDERED_CHART_POINTS = 1_000;

const firstDateOnOrAfter = (dates: string[], target: string) => {
    let lower = 0;
    let upper = dates.length;

    while (lower < upper) {
        const middle = Math.floor((lower + upper) / 2);

        if (dates[middle] < target) {
            lower = middle + 1;
        } else {
            upper = middle;
        }
    }

    return dates[lower];
};

const lastDateOnOrBefore = (dates: string[], target: string) => {
    let lower = 0;
    let upper = dates.length;

    while (lower < upper) {
        const middle = Math.floor((lower + upper) / 2);

        if (dates[middle] <= target) {
            lower = middle + 1;
        } else {
            upper = middle;
        }
    }

    return dates[lower - 1];
};

export const resolveFinancialOverviewChartEvents = (
    events: FinancialOverviewChartEvent[],
    data: FinancialOverviewChartDataPoint[],
) => {
    const chartDates = Array.from(new Set(data.map((point) => String(point.name ?? '')).filter(Boolean))).sort();

    return events.flatMap((event): FinancialOverviewChartEvent[] => {
        const chartDate = firstDateOnOrAfter(chartDates, event.date);
        const chartEndDate = event.endDate ? lastDateOnOrBefore(chartDates, event.endDate) : undefined;

        if (!chartDate || (event.endDate && (!chartEndDate || chartEndDate < chartDate))) {
            return [];
        }

        return [{ ...event, chartDate, ...(chartEndDate ? { chartEndDate } : {}) }];
    });
};

const numericValue = (value: unknown) => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
};

const metricValue = (point: FinancialOverviewTickerDataPoint, metric: FinancialOverviewChartMetric) => {
    if (metric === 'volume') {
        return numericValue(point.volume);
    }

    if (metric === 'changePercent') {
        return numericValue(point.changePercent ?? point.changePercentage ?? point.changesPercentage ?? point.change);
    }

    return numericValue(point.close);
};

/**
 * Keeps the first/last observations and the primary-series extrema from each bucket.
 * This bounds the SVG work Recharts performs while preserving visually meaningful peaks and troughs.
 */
export const downsampleFinancialOverviewChartData = (
    data: FinancialOverviewChartDataPoint[],
    maxPoints = MAX_RENDERED_CHART_POINTS,
) => {
    if (data.length <= maxPoints || maxPoints < 4) {
        return data;
    }

    const interiorBucketCount = Math.max(1, Math.floor((maxPoints - 2) / 2));
    const interiorLength = data.length - 2;
    const sampled: FinancialOverviewChartDataPoint[] = [data[0]];

    for (let bucketIndex = 0; bucketIndex < interiorBucketCount; bucketIndex += 1) {
        const start = 1 + Math.floor((bucketIndex * interiorLength) / interiorBucketCount);
        const end = Math.min(
            data.length - 1,
            1 + Math.floor(((bucketIndex + 1) * interiorLength) / interiorBucketCount),
        );
        let minimumIndex = start;
        let maximumIndex = start;

        for (let index = start + 1; index < end; index += 1) {
            const value = Number(data[index].price);
            const minimum = Number(data[minimumIndex].price);
            const maximum = Number(data[maximumIndex].price);

            if (Number.isFinite(value) && (!Number.isFinite(minimum) || value < minimum)) {
                minimumIndex = index;
            }

            if (Number.isFinite(value) && (!Number.isFinite(maximum) || value > maximum)) {
                maximumIndex = index;
            }
        }

        if (minimumIndex === maximumIndex) {
            sampled.push(data[minimumIndex]);
        } else if (minimumIndex < maximumIndex) {
            sampled.push(data[minimumIndex], data[maximumIndex]);
        } else {
            sampled.push(data[maximumIndex], data[minimumIndex]);
        }
    }

    sampled.push(data[data.length - 1]);

    return sampled;
};

export const buildFinancialOverviewChartData = (
    tickerData: FinancialOverviewTickerDataPoint[],
    comparisonData: Record<string, FinancialOverviewTickerDataPoint[]>,
    comparisonSymbols: string[],
    metric: FinancialOverviewChartMetric,
) => {
    const rows = new Map<string, FinancialOverviewChartDataPoint>();

    tickerData.forEach((point, index) => {
        const name = point.date;

        if (!name) {
            return;
        }

        const current = metricValue(point, metric);
        const previous = index ? metricValue(tickerData[index - 1], metric) : undefined;

        rows.set(name, {
            name,
            previousChange: current !== undefined && previous !== undefined ? current - previous : 0,
            price: current,
            volume: numericValue(point.volume),
        });
    });

    comparisonSymbols.forEach((symbol) => {
        comparisonData[symbol]?.forEach((point) => {
            if (!point.date) {
                return;
            }

            const row = rows.get(point.date);

            if (row) {
                row[`comparison:${symbol}`] = metricValue(point, metric);
            }
        });
    });

    return downsampleFinancialOverviewChartData(Array.from(rows.values()));
};
